/* ============================================================
   ATHLO+ — Storefront enhancements (ADDON)
   Cart drawer · predictive search · AJAX quick-add.
   Progressive enhancement: every feature falls back to a normal
   page if its markup isn't rendered or a request fails.
   Remove this file (and its <script> in theme.liquid) to revert.
   ============================================================ */
(function () {
  'use strict';

  /* ---------- escape untrusted strings before innerHTML ---------- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------- a11y: polite screen-reader announcements (aria-live) ----------
     Shared, lazily-created live region. window.__hubAnnounce(msg) speaks `msg`
     to assistive tech (e.g. "Item added to your bag") without any visual change.
     Reused by the quiz athlete-card add path too (guarded — no-ops if absent). */
  var hubLive = null;
  window.__hubAnnounce = function (msg) {
    try {
      if (!hubLive) {
        hubLive = document.createElement('div');
        hubLive.className = 'sr-only';
        hubLive.setAttribute('aria-live', 'polite');
        hubLive.setAttribute('aria-atomic', 'true');
        hubLive.setAttribute('data-hub-live', '');
        (document.body || document.documentElement).appendChild(hubLive);
      }
      hubLive.textContent = '';                          // reset so identical repeats re-announce
      setTimeout(function () { hubLive.textContent = msg; }, 60);
    } catch (e) { /* a11y is best-effort */ }
  };

  /* ---------- money ---------- */
  const drawer = document.querySelector('[data-cart-drawer]');
  const moneyFormat = (drawer && drawer.getAttribute('data-money-format')) || '${{amount}}';
  function formatMoney(cents) {
    const value = (cents || 0) / 100.0;
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, key) {
      switch (key) {
        case 'amount': return value.toFixed(2);
        case 'amount_no_decimals': return value.toFixed(0);
        case 'amount_with_comma_separator': return value.toFixed(2).replace('.', ',');
        case 'amount_no_decimals_with_comma_separator': return value.toFixed(0).replace('.', ',');
        default: return value.toFixed(2);
      }
    });
  }
  function sized(url, w) { if (!url) return ''; return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w; }

  /* ---------- overlay helpers ---------- */
  function lockScroll(on) { document.documentElement.style.overflow = on ? 'hidden' : ''; document.documentElement.style.touchAction = on ? 'none' : ''; document.body.classList.toggle('overlay-open', !!on); }
  function openEl(el) { if (!el) return; el.classList.add('is-open'); el.setAttribute('aria-hidden', 'false'); lockScroll(true); trapFocus(el); }
  function closeEl(el) {
    if (!el) return; el.classList.remove('is-open'); el.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('.cart-drawer.is-open, .search-panel.is-open')) lockScroll(false);
    releaseFocus(el);
  }

  /* ---------- FOCUS TRAP ---------- */
  const focusStack = [];
  let focusHandler = null;
  function trapFocus(container) {
    const prev = document.activeElement;
    focusStack.push({ container: container, prev: prev });
    const all = container.querySelectorAll('a[href]:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])');
    if (!all.length) return;
    const first = all[0], last = all[all.length - 1];
    /* ADDON (a11y 2026-08-06): FOCUS SOMETHING THAT CAN ACTUALLY TAKE FOCUS.
       `[data-cart-close]` matches the OVERLAY DIV first — the click-outside-to-
       close layer wears the same attribute as the close button. A <div> with no
       tabindex is not focusable, so .focus() was a silent no-op and the cart
       drawer opened with focus stranded on the header icon behind it. A
       keyboard user's next Tab then walked the page BEHIND the open overlay.
       The trap's Tab-wrapping never engaged either, because it only wraps once
       focus is already inside.

       This is the third instance of one bug today: .focus() called on something
       that cannot hold it (the mobile menu dialog had no tabindex; the booking
       slot was destroyed by a re-render). The lesson is the assertion, not the
       selector — so rather than just naming a better element, VERIFY the focus
       landed and fall back if it did not. That is self-healing for the next
       element somebody marks up as a div. */
    const target = container.querySelector('[data-search-input]')
                || container.querySelector('button[data-cart-close], a[data-cart-close]')
                || first;
    try { target.focus(); } catch (e) { /* fall through to the check below */ }
    if (!container.contains(document.activeElement)) {
      try { first.focus(); } catch (e) {}
    }
    focusHandler = function (e) {
      if (e.key !== 'Tab') return;
      const focusable = Array.from(container.querySelectorAll('a[href]:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'));
      if (!focusable.length) { e.preventDefault(); return; }
      const f = focusable[0], l = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === f) { e.preventDefault(); l.focus(); }
      else if (!e.shiftKey && document.activeElement === l) { e.preventDefault(); f.focus(); }
    };
    container.addEventListener('keydown', focusHandler);
  }
  function releaseFocus(container) {
    const entry = focusStack.pop();
    if (entry && entry.prev && entry.prev.focus) entry.prev.focus();
    if (focusHandler) { container.removeEventListener('keydown', focusHandler); focusHandler = null; }
  }

  /* ---------- CART DRAWER ---------- */
  function initCart() {
    if (!drawer) return;
    const itemsEl = drawer.querySelector('[data-cart-items]');
    // Excludes [data-hub-save-count]: the header's saved-items badge also carries .cart-count,
    // so the cart used to overwrite the saved count with the number of items in the bag.
    const countEls = document.querySelectorAll('[data-cart-count]:not([data-hub-save-count]), .cart-count:not([data-hub-save-count])');
    const subtotalEl = drawer.querySelector('[data-cart-subtotal]');
    const footEl = drawer.querySelector('[data-cart-foot]');
    const emptyEl = drawer.querySelector('[data-cart-empty]');
    const ship = drawer.querySelector('[data-cart-ship]');
    const upsellEl = drawer.querySelector('[data-cart-upsell]');
    let upsellCache = { seed: null, products: [] };
    let lastTotal = null;

    function open() {
      openEl(drawer);
      // One-shot entrance: items stagger in only on OPEN, so a later qty tweak doesn't
      // re-animate the whole list. The class is scoped-away after the stagger window.
      drawer.classList.add('just-opened');
      setTimeout(function () { drawer.classList.remove('just-opened'); }, 700);
      refresh();
    }
    function close() { closeEl(drawer); }

    function renderItems(cart) {
      itemsEl.innerHTML = cart.items.map(function (it, i) {
        const img = it.image ? '<img src="' + sized(it.image, 160) + '" alt="" width="64" height="80" loading="lazy">' : '';
        const variant = (it.variant_title && it.variant_title !== 'Default Title') ? '<span class="cart-item__variant">' + esc(it.variant_title) + '</span>' : '';
        // A BOOKED SESSION IS ONE SEAT AND CANNOT BE MULTIPLIED. The line carries exactly one hold
        // id, the backend confirms exactly that one hold, and the price is multiplied by quantity -
        // so a "+" here charges twice for one seat. Raising it to 4 charged 4 x $228 and booked one
        // person. Four athletes need four separate times, which is four trips through the picker.
        const booked = !!(it.properties && it.properties['_booking_hold_id']);
        const qty = booked
          ? '<span class="cart-qty__fixed" title="One session, one seat. Book another time for another athlete.">' + esc(it.quantity) + '</span>'
          : '<button type="button" data-qty-down aria-label="Decrease">&minus;</button><span data-qty>' + esc(it.quantity) + '</span><button type="button" data-qty-up aria-label="Increase">+</button>';
        return '<div class="cart-item" data-cart-item style="--i:' + i + '" data-key="' + esc(it.key) + '"' + (booked ? ' data-booking="1"' : '') + '>' +
          '<a href="' + esc(it.url) + '" class="cart-item__media">' + img + '</a>' +
          '<div class="cart-item__info"><a href="' + esc(it.url) + '" class="cart-item__title">' + esc(it.product_title) + '</a>' + variant +
            '<div class="cart-item__bottom"><div class="cart-qty" data-key="' + esc(it.key) + '">' + qty +
            '</div><span class="cart-item__price">' + formatMoney(it.final_line_price) + '</span></div></div>' +
          '<button class="cart-item__remove" type="button" data-cart-remove="' + esc(it.key) + '" aria-label="Remove">&times;</button></div>';
      }).join('');
    }

    function paint(cart) {
      // /cart.js can answer with an error payload (throttling, a 4xx from a stale
      // session). Painting it produced a header reading "(undefined)" and a $0.00
      // subtotal over the real items, so bail out and leave the last good state up.
      if (!cart || typeof cart.item_count !== 'number' || !Array.isArray(cart.items)) {
        drawer.classList.add('has-error');
        window.__hubAnnounce && window.__hubAnnounce('Could not refresh your bag');
        return;
      }
      drawer.classList.remove('has-error');
      countEls.forEach(function (e) { e.textContent = e.hasAttribute('data-cart-count') ? '(' + cart.item_count + ')' : cart.item_count; });
      if (subtotalEl) {
        subtotalEl.textContent = formatMoney(cart.total_price);
        // Pulse the subtotal when it actually changes (not on first paint).
        if (lastTotal !== null && lastTotal !== cart.total_price) {
          subtotalEl.classList.remove('is-bumped');
          void subtotalEl.offsetWidth; // reflow so the animation can restart
          subtotalEl.classList.add('is-bumped');
        }
      }
      lastTotal = cart.total_price;
      // ADDON (members-only points, 2026-09-30): say the bag changed. assets/hub-cart-points.js
      // repaints its "You'll earn X points" line from this; nothing else listens, nothing depends on it.
      try { document.dispatchEvent(new CustomEvent('hub:cart', { detail: cart })); } catch (e) {}
      const empty = cart.item_count === 0;
      if (footEl) footEl.hidden = empty;
      if (emptyEl) emptyEl.hidden = !empty;
      renderItems(cart);
      renderUpsell(cart);
      if (ship) {
        const threshold = parseInt(ship.getAttribute('data-threshold'), 10) || 0;
        const bar = ship.querySelector('[data-ship-bar]'), msg = ship.querySelector('[data-ship-msg]');
        // 2026-10-06: a bag where NOTHING ships (only services - a massage, a test, a coaching plan) used to
        // read "You've unlocked free shipping" on a session. Hidden then; an EMPTY bag keeps the bar as the
        // nudge it was built to be. cart.requires_shipping is Shopify's own "any line ships". Inline display,
        // not [hidden], so no .cart-ship display rule can override it. Same rule on the cart page.
        const nothingShips = cart.item_count > 0 && cart.requires_shipping === false;
        ship.style.display = nothingShips ? 'none' : '';
        if (!nothingShips && threshold > 0 && bar && msg) {
          bar.style.width = Math.min(100, (cart.total_price / threshold) * 100) + '%';
          msg.innerHTML = cart.total_price >= threshold
            ? "You've unlocked <b>free shipping</b>"
            : "You're <b>" + formatMoney(threshold - cart.total_price) + "</b> away from free shipping";
          // 2026-09-30 launch fix: AporeNaturals ships on its own profile, a flat $4.50 that is never
          // free (policy: "All AporeNaturals Products $4.50"; measured: two Apore lines = one $4.50).
          // Mixed bag: keep the threshold line, add the Apore line. Apore-only bag: the threshold line
          // would be false ("unlocked free shipping" while paying $4.50), so show only the Apore line.
          // Case-SENSITIVE /Apore/ on purpose: 'Therabody Singapore' contains lowercase "apore".
          const aporeLines = cart.items.filter(function (it) { return /Apore/.test(String(it.vendor || '')); }).length;
          if (aporeLines) {
            const aporeNote = 'AporeNaturals items always ship at a flat $4.50.';
            msg.innerHTML = aporeLines === cart.items.length ? aporeNote : msg.innerHTML + '<br>' + aporeNote;
          }
        }
      }
    }

    // === ADDON: cart upsell. Recommends against the most-recently-added line using
    // Shopify's own product recommendations. Honest by design: renders nothing when the
    // API returns no AVAILABLE, not-already-in-cart products, so it never invents a shelf.
    function renderUpsell(cart) {
      if (!upsellEl) return;
      const rowEl = upsellEl.querySelector('[data-cart-upsell-row]');
      if (!cart || cart.item_count === 0) { upsellEl.hidden = true; upsellCache = { seed: null, products: [] }; return; }
      const inCart = {};
      cart.items.forEach(function (it) { inCart[it.product_id] = true; });
      const seed = cart.items[cart.items.length - 1].product_id;
      // Render from the cached recs for this seed, filtered against the LIVE cart every time,
      // so adding an upsell item drops it from the shelf at once (a backfill takes its place).
      // === ADDON 2026-10-26: gap filler. Under the free-delivery threshold, the cheapest one-click item
      // that closes the gap goes first and is tagged. Same maths as the progress bar above (cart.total_price
      // vs data-threshold). Excluded: services (they never ship), multi-variant items (not one click) and
      // AporeNaturals (its own flat $4.50 profile is never free). Remove this function + its call to roll back.
      function orderForGap(pool, cart) {
        const gapThreshold = ship ? (parseInt(ship.getAttribute('data-threshold'), 10) || 0) : 0;
        const gap = gapThreshold > 0 ? gapThreshold - cart.total_price : 0;
        if (gap <= 0) return { pool: pool, closer: null };
        const oneClickShip = function (p) {
          const vs = p.variants || [];
          return vs.length === 1 && vs[0].available && vs[0].requires_shipping !== false &&
            String(p.vendor || '').indexOf('Apore') === -1 && String(p.type || '').toLowerCase().indexOf('service') === -1;
        };
        const fits = pool.filter(function (p) { return oneClickShip(p) && p.variants[0].price >= gap; })
          .sort(function (a, b) { return a.variants[0].price - b.variants[0].price; });
        if (!fits.length) return { pool: pool, closer: null };
        const first = fits[0];
        return { pool: [first].concat(pool.filter(function (p) { return p.id !== first.id; })), closer: first.id };
      }
      function paintShelf() {
        const ordered = orderForGap(upsellCache.products.filter(function (p) { return p.available && !inCart[p.id]; }), cart);
        const closer = ordered.closer;
        const recs = ordered.pool.slice(0, 3);
        if (!recs.length) { upsellEl.hidden = true; return; }
        rowEl.innerHTML = recs.map(function (p) {
          let v = null;
          for (let i = 0; i < (p.variants || []).length; i++) { if (p.variants[i].available) { v = p.variants[i]; break; } }
          if (!v) v = (p.variants || [])[0];
          if (!v) return '';
          const img = p.featured_image || (p.images && p.images[0]);
          const media = img ? '<img src="' + sized(img, 120) + '" alt="" width="48" height="48" loading="lazy">' : '';
          // One click only when one click decides — the same rule as snippets/hub-card-cta.liquid.
          // A service (a booking needs its slot, waiver and phone on the product page — order #1006)
          // or anything that doesn't ship links to its page; so does a product with options to pick.
          const type = String(p.type || '').toLowerCase().trim();
          const service = type === 'services' || type === 'service' ||
            (p.variants || []).some(function (x) { return x.requires_shipping === false; });
          const cta = service
            ? '<a class="cart-upsell__add" href="' + esc(p.url) + '" style="display:inline-flex;align-items:center" data-upsell-link="view" aria-label="View details for ' + esc(p.title) + '">View</a>'
            : ((p.variants || []).length > 1
              ? '<a class="cart-upsell__add" href="' + esc(p.url) + '" style="display:inline-flex;align-items:center" data-upsell-link="options" aria-label="Choose options for ' + esc(p.title) + '">Choose</a>'
              : '<button type="button" class="cart-upsell__add" data-upsell-add="' + v.id + '" aria-label="Add ' + esc(p.title) + ' to bag">Add</button>');
          return '<div class="cart-upsell__item">' +
            '<a href="' + esc(p.url) + '" class="cart-upsell__media">' + media + '</a>' +
            '<div class="cart-upsell__info"><a href="' + esc(p.url) + '" class="cart-upsell__name">' + esc(p.title) + '</a>' +
            '<span class="cart-upsell__price">' + formatMoney(v.price) + (p.id === closer ? ' · Gets you free delivery' : '') + '</span></div>' +
            cta +
            '</div>';
        }).join('');
        upsellEl.hidden = false;
      }
      // Same seed product: just re-filter the cached recs (no refetch on qty/add churn).
      if (seed === upsellCache.seed) { paintShelf(); return; }
      upsellCache = { seed: seed, products: [] };
      fetch('/recommendations/products.json?product_id=' + seed + '&limit=10&intent=related', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (data) { upsellCache.products = (data.products || []); paintShelf(); })
        .catch(function () { upsellEl.hidden = true; });
    }

    function refresh() {
      drawer.classList.add('is-loading');
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error('cart ' + r.status); return r.json(); })
        .then(paint).catch(function () { paint(null); })
        .then(function () { drawer.classList.remove('is-loading'); });
    }
    function change(key, qty) {
      drawer.classList.add('is-loading');
      fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity: qty }) })
        .then(function (r) { if (!r.ok) throw new Error('change ' + r.status); return r.json(); })
        .then(paint).catch(function () { paint(null); })
        .then(function () { drawer.classList.remove('is-loading'); });
    }

    window.__hubCartAdd = function (formDataOrId) {
      const opts = (typeof formDataOrId === 'string' || typeof formDataOrId === 'number')
        ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: formDataOrId, quantity: 1 }] }) }
        : { method: 'POST', headers: { Accept: 'application/json' }, body: formDataOrId };
      return fetch('/cart/add.js', opts).then(function (r) { return r.json(); }).then(function (res) {
        if (res && res.status && res.status >= 400) throw res;
        open();
        window.__hubAnnounce && window.__hubAnnounce('Item added to your bag');
        return res;
      });
    };

    drawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-cart-close]')) { e.preventDefault(); return close(); }
      const addBtn = e.target.closest('[data-upsell-add]');
      if (addBtn) {
        e.preventDefault();
        addBtn.disabled = true;
        window.__hubCartAdd(addBtn.getAttribute('data-upsell-add'))
          .then(function () { addBtn.disabled = false; })
          .catch(function () { addBtn.disabled = false; });
        return;
      }
      const item = e.target.closest('.cart-item'); if (!item) return;
      const key = item.getAttribute('data-key');
      if (e.target.closest('[data-cart-remove]')) { e.preventDefault(); return change(key, 0); }
      // Belt and braces: the button is not rendered for a booking line, but a stale drawer painted
      // before this shipped still has one in the DOM, and the cost of being wrong here is a customer
      // charged twice for one seat.
      if (item.getAttribute('data-booking') === '1' && e.target.closest('[data-qty-up]')) return;
      if (e.target.closest('[data-qty-up]')) { return change(key, parseInt(item.querySelector('[data-qty]').textContent, 10) + 1); }
      if (e.target.closest('[data-qty-down]')) { return change(key, Math.max(0, parseInt(item.querySelector('[data-qty]').textContent, 10) - 1)); }
    });

    document.querySelectorAll('[data-cart-toggle]').forEach(function (a) {
      a.addEventListener('click', function (e) { e.preventDefault(); open(); });
    });
    // AJAX-add any product form (quick-add behaviour) and open the drawer
    document.querySelectorAll('form[action$="/cart/add"]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        const btn = form.querySelector('[type=submit]'); if (btn) btn.disabled = true;
        window.__hubCartAdd(new FormData(form))
          .then(function () { if (btn) btn.disabled = false; })
          .catch(function () { if (btn) btn.disabled = false; form.submit(); });
      });
    });
    window.__hubCartOpen = open;
  }

  /* ---------- PREDICTIVE SEARCH ---------- */
  function initSearch() {
    const panel = document.querySelector('[data-search-panel]'); if (!panel) return;
    const input = panel.querySelector('[data-search-input]');
    const results = panel.querySelector('[data-search-results]');
    const clearBtn = panel.querySelector('[data-search-clear]');
    function open() { openEl(panel); setTimeout(function () { input && input.focus(); }, 200); }
    function close() { closeEl(panel); }
    document.querySelectorAll('[data-search-toggle]').forEach(function (a) {
      a.addEventListener('click', function (e) { e.preventDefault(); open(); });
    });
    panel.querySelectorAll('[data-search-close]').forEach(function (b) { b.addEventListener('click', function (e) { e.preventDefault(); close(); }); });
    if (clearBtn) clearBtn.addEventListener('click', function () {
      if (input) { input.value = ''; input.focus(); results.innerHTML = ''; }
      clearBtn.hidden = true;
    });
    let timer = null;
    if (input) input.addEventListener('input', function () {
      const q = input.value.trim(); clearTimeout(timer);
      if (clearBtn) clearBtn.hidden = (q.length === 0);
      if (q.length < 2) { results.innerHTML = ''; return; }
      timer = setTimeout(function () {
        fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            const items = (data.resources && data.resources.results && data.resources.results.products) || [];
            if (!items.length) { results.innerHTML = '<div class="search-results__empty">No matches — press Enter to search everything.</div>'; return; }
            results.innerHTML = items.map(function (p) {
              const img = (p.featured_image && p.featured_image.url) ? '<img src="' + sized(p.featured_image.url, 92) + '" alt="" width="46" height="56" loading="lazy">' : '<span class="search-result__ph" aria-hidden="true"></span>';
              const sub = p.vendor ? '<span class="search-result__p">' + esc(p.vendor) + '</span>' : '';
              return '<a class="search-result" href="' + esc(p.url) + '">' + img + '<span><span class="search-result__t">' + esc(p.title) + '</span>' + (sub ? '<br>' + sub : '') + '</span></a>';
            }).join('');
          }).catch(function () {});
      }, 220);
    });
  }

  /* ---------- global ESC ---------- */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    const d = document.querySelector('.cart-drawer.is-open'), s = document.querySelector('.search-panel.is-open');
    if (d) closeEl(d); if (s) closeEl(s);
  });

  /* ---------- QUICK-ADD (data-quick-add="variantId") ---------- */
  function initQuickAdd() {
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-quick-add]'); if (!btn) return;
      e.preventDefault();
      const id = btn.getAttribute('data-quick-add'); if (!id) return;
      if (window.__hubCartAdd) {
        btn.disabled = true; const t = btn.textContent; btn.textContent = '…';
        window.__hubCartAdd(id)
          .then(function () { btn.textContent = '✓'; setTimeout(function () { btn.textContent = t; btn.disabled = false; }, 1100); })
          .catch(function () { btn.textContent = t; btn.disabled = false; });
      } else { window.location.href = '/cart'; }
    });
  }

  function boot() { initCart(); initSearch(); initQuickAdd(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
